import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, Select, OutlinedInput, MenuItem, Chip, Box, Typography, IconButton, Checkbox, Grid, Popover, TextField, Tooltip, FormControlLabel, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Close, Clear, Comment, ExpandMore } from '@mui/icons-material';

import useReactHookFormStyles from '../../../../../custom-components/form-components/ReactHookForm.styles';
import FormInput from '../../input/FormInput';
import BooleanSelect from '../../boolean/BooleanSelect';
import { createRapidTestStandardsSchema } from './RapidTestStandardsInput.schema';
import RapidTestComponents from './components';
import { getDefaultDecimalPlaces } from './rapid-test-precision';
import { getUserRegion } from '../../../../../../utils';
import Region from '../../../../../constants/region';

const NoteButton = ({ note, onNoteChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const hasNote = !!note;

    const handleChange = (e) => {
        onNoteChange(e.target.value || null);
    };

    const handleClear = () => {
        onNoteChange(null);
        setAnchorEl(null);
    };

    return (
        <>
            <Tooltip title={hasNote ? note : 'Add note'} arrow>
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5, color: hasNote ? 'primary.main' : 'text.disabled' }}>
                    <Comment fontSize="small" />
                </IconButton>
            </Tooltip>
            <Popover open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Box sx={{ p: 2, width: 280 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Add a note
                    </Typography>
                    <TextField value={note || ''} onChange={handleChange} size="small" fullWidth multiline minRows={2} placeholder="Enter note..." autoFocus />
                    {hasNote && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <IconButton size="small" onClick={handleClear} sx={{ color: 'text.secondary' }} title="Clear note">
                                <Clear fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Popover>
        </>
    );
};

const RapidTestStandardsInput = ({ name, props: { label = 'Rapid Test Standards', required, validationSchema } }) => {
    const classes = useReactHookFormStyles();

    const {
        control,
        formState: { errors },
        watch,
        clearErrors,
        setError,
    } = useFormContext();

    // COA Settings (Show on COA Report) is UDA-only.
    const isUda = getUserRegion() === Region.UDA;

    const currentValues = watch(name) || {};
    // Stringify to detect nested object changes
    const currentValuesJson = JSON.stringify(currentValues);

    useEffect(() => {
        if (Object.keys(currentValues).length > 0) {
            const schema = validationSchema || createRapidTestStandardsSchema();

            // Clear existing errors for this field first
            clearErrors(name);

            schema
                .validate(currentValues, { abortEarly: false })
                .then(() => {
                    // Validation passed, errors already cleared
                })
                .catch((validationError) => {
                    if (validationError.inner) {
                        validationError.inner.forEach((err) => {
                            const fieldPath = `${name}.${err.path}`;
                            setError(fieldPath, {
                                type: 'validation',
                                message: err.message,
                            });
                        });
                    }
                });
        } else {
            clearErrors(name);
        }
    }, [currentValuesJson, name, validationSchema, clearErrors, setError]);

    const nestedName = name.split('.');
    const error = errors[name] || (nestedName.length && nestedName.reduce((acc, key) => acc?.[key], errors));

    const getFieldError = (componentKey, field) => {
        const fieldPath = `${name}.${componentKey}.${field}`;
        return fieldPath.split('.').reduce((acc, key) => acc?.[key], errors);
    };

    const renderNumericRangeField = (component, handleDelete, handleRequiredChange, handleNoteChange, fieldValue) => {
        const minError = getFieldError(component.key, 'min');
        const maxError = getFieldError(component.key, 'max');
        const isRequired = fieldValue?.required !== false;

        return (
            <>
                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {component.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NoteButton note={fieldValue?.note} onNoteChange={(note) => handleNoteChange(component.key, note)} />
                        <Checkbox checked={isRequired} onChange={(e) => handleRequiredChange(component.key, e.target.checked)} size="small" sx={{ p: 0.5 }} title="Required" />
                        <IconButton size="small" onClick={() => handleDelete(component.key)} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }} title={`Remove ${component.label}`}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Input row */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormInput
                        name={`${name}.${component.key}.min`}
                        props={{
                            label: 'Min',
                            type: 'number',
                            variant: 'outlined',
                            size: 'small',
                            valueAsNumber: true,
                            placeholder: 'None',
                            error: !!minError,
                            helperText: minError?.message,
                            fullWidth: true,
                            inputProps: { step: 0.01 },
                            sx: { bgcolor: 'background.paper' },
                        }}
                    />
                    <FormInput
                        name={`${name}.${component.key}.max`}
                        props={{
                            label: 'Max',
                            type: 'number',
                            variant: 'outlined',
                            size: 'small',
                            valueAsNumber: true,
                            placeholder: 'None',
                            error: !!maxError,
                            helperText: maxError?.message,
                            fullWidth: true,
                            inputProps: { step: 0.01 },
                            sx: { bgcolor: 'background.paper' },
                        }}
                    />
                </Box>
            </>
        );
    };

    const renderBooleanField = (component, handleDelete, handleRequiredChange, handleNoteChange, fieldValue) => {
        const fieldError = getFieldError(component.key, 'required_value');
        const isRequired = fieldValue?.required !== false;

        return (
            <>
                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {component.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <NoteButton note={fieldValue?.note} onNoteChange={(note) => handleNoteChange(component.key, note)} />
                        <Checkbox checked={isRequired} onChange={(e) => handleRequiredChange(component.key, e.target.checked)} size="small" sx={{ p: 0.5 }} title="Required" />
                        <IconButton size="small" onClick={() => handleDelete(component.key)} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }} title={`Remove ${component.label}`}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Input row */}
                <BooleanSelect
                    name={`${name}.${component.key}.required_value`}
                    props={{
                        label: 'Pass Condition',
                        variant: 'outlined',
                        size: 'small',
                        placeholder: 'Any value',
                        error: !!fieldError,
                        helperText: fieldError?.message,
                        fullWidth: true,
                        optionMap: {
                            true: component.trueLabel ? `Must be ${component.trueLabel}` : 'Must be Positive',
                            false: component.falseLabel ? `Must be ${component.falseLabel}` : 'Must be Negative',
                            empty: 'Any value',
                        },
                        sx: { bgcolor: 'background.paper' },
                    }}
                />
            </>
        );
    };

    return (
        <FormControl className={classes.formController} error={!!error}>
            <Controller
                name={name}
                control={control}
                defaultValue={{}}
                render={({ field }) => {
                    const selectedComponents = Object.keys(field.value || {});
                    const selectedComponentObjects = RapidTestComponents.filter((comp) => selectedComponents.includes(comp.key));

                    const handleComponentSelection = (event) => {
                        const { value } = event.target;
                        const newValue = {};

                        value.forEach((componentKey) => {
                            const component = RapidTestComponents.find((c) => c.key === componentKey);
                            if (component) {
                                // Keep existing value if present, otherwise create default with required: true
                                newValue[componentKey] =
                                    field.value?.[componentKey] ||
                                    (component.type === 'numeric'
                                        ? { min: null, max: null, required: true, unlimited_retests: true, max_retests: 0, fails_rapid_test: true, qa_overrideable: true, requires_two_qa_tests: false }
                                        : { required_value: null, required: true, unlimited_retests: true, max_retests: 0, fails_rapid_test: true, qa_overrideable: true, requires_two_qa_tests: false });
                            }
                        });

                        field.onChange(newValue);
                    };

                    const handleDeleteComponent = (componentKey) => {
                        const newValue = { ...field.value };
                        delete newValue[componentKey];
                        field.onChange(newValue);
                    };

                    const handleRequiredChange = (componentKey, isRequired) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        field.onChange({
                            ...current,
                            [componentKey]: {
                                ...currentComponent,
                                required: isRequired,
                            },
                        });
                    };

                    const handleNoteChange = (componentKey, note) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        field.onChange({
                            ...current,
                            [componentKey]: {
                                ...currentComponent,
                                note,
                            },
                        });
                    };

                    const handleClearAll = (event) => {
                        event.stopPropagation();
                        field.onChange({});
                    };

                    const setValue = (componentKey, key, value) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        field.onChange({
                            ...current,
                            [componentKey]: {
                                ...currentComponent,
                                [key]: value,
                                ...(key === 'unlimited_retests' && !!value && { max_retests: 0 }),
                            },
                        });
                    };

                    // Set a value inside the component's nested `coa_settings` object.
                    // path is ['show'] or ['floor', 'enable'] / ['ceiling', 'threshold'] etc.
                    const setCoaSetting = (componentKey, path, value) => {
                        const current = field.value ?? {};
                        const currentComponent = current[componentKey] ?? {};
                        const coa = currentComponent.coa_settings ?? {};
                        const nextCoa = path.length === 2 ? { ...coa, [path[0]]: { ...(coa[path[0]] ?? {}), [path[1]]: value } } : { ...coa, [path[0]]: value };
                        field.onChange({
                            ...current,
                            [componentKey]: { ...currentComponent, coa_settings: nextCoa },
                        });
                    };

                    return (
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {label}
                                {required && <span style={{ color: 'red' }}> *</span>}
                            </Typography>

                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <Select
                                    multiple
                                    value={selectedComponents}
                                    onChange={handleComponentSelection}
                                    input={<OutlinedInput />}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <em style={{ color: '#999' }}>Select Components to Test</em>;
                                        }
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                    overflow: 'hidden',
                                                    width: '100%',
                                                }}
                                            >
                                                {selected.map((value) => {
                                                    const component = RapidTestComponents.find((comp) => comp.key === value);
                                                    return (
                                                        <Chip
                                                            key={value}
                                                            label={component?.label || value}
                                                            size="small"
                                                            onDelete={(event) => {
                                                                event.stopPropagation();
                                                                handleDeleteComponent(value);
                                                            }}
                                                            deleteIcon={<Close fontSize="small" />}
                                                            sx={{
                                                                maxWidth: '150px',
                                                                '& .MuiChip-label': {
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        );
                                    }}
                                    endAdornment={
                                        selectedComponents.length > 0 && (
                                            <IconButton
                                                onClick={handleClearAll}
                                                size="small"
                                                title="Clear All"
                                                sx={{
                                                    color: 'text.secondary',
                                                    mr: 1,
                                                    position: 'absolute',
                                                    right: 32,
                                                    zIndex: 1,
                                                }}
                                            >
                                                <Clear />
                                            </IconButton>
                                        )
                                    }
                                    sx={{
                                        '& .MuiSelect-select': {
                                            minHeight: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            overflow: 'hidden',
                                            paddingRight: selectedComponents.length > 0 ? '60px !important' : undefined,
                                        },
                                    }}
                                >
                                    {RapidTestComponents.map((component) => (
                                        <MenuItem key={component.key} value={component.key}>
                                            {component.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {selectedComponentObjects.length > 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                            Configure Standards
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Checkbox checked size="small" sx={{ p: 0, cursor: 'default' }} disableRipple />
                                            <Typography variant="caption" color="text.secondary">
                                                = Required
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={2}>
                                        {selectedComponentObjects.map((component) => {
                                            const componentStandard = field.value?.[component.key] || {};
                                            const qaEnabledCount = [componentStandard.fails_rapid_test, componentStandard.qa_overrideable, componentStandard.requires_two_qa_tests].filter(Boolean).length;
                                            const showOnCoa = componentStandard.coa_settings?.show !== false;
                                            const coaFloorEnabled = !!componentStandard.coa_settings?.floor?.enable;
                                            const coaCeilingEnabled = !!componentStandard.coa_settings?.ceiling?.enable;
                                            const coaTotal = component.type === 'numeric' ? 3 : 1;
                                            const coaEnabledCount = (showOnCoa ? 1 : 0) + (component.type === 'numeric' ? (coaFloorEnabled ? 1 : 0) + (coaCeilingEnabled ? 1 : 0) : 0);
                                            const decimalPlacesError = getFieldError(component.key, 'decimal_places');
                                            const defaultDecimalPlaces = getDefaultDecimalPlaces(component.key);
                                            return (
                                                <Grid item xs={12} md={6} key={component.key}>
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            bgcolor: 'grey.50',
                                                            borderRadius: 1,
                                                            border: 1,
                                                            borderColor: 'grey.200',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        {component.type === 'numeric' ? renderNumericRangeField(component, handleDeleteComponent, handleRequiredChange, handleNoteChange, field.value?.[component.key]) : renderBooleanField(component, handleDeleteComponent, handleRequiredChange, handleNoteChange, field.value?.[component.key])}
                                                        {component.type === 'numeric' && (
                                                            <Accordion disableGutters elevation={0} square sx={{ mt: 1, bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                                                <AccordionSummary expandIcon={<ExpandMore fontSize="small" />} sx={{ minHeight: 0, p: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="caption" fontWeight="medium">
                                                                        General Settings
                                                                    </Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 0 }}>
                                                                    <Box sx={{ width: 'calc(50% - 4px)' }}>
                                                                        <FormInput
                                                                            name={`${name}.${component.key}.decimal_places`}
                                                                            props={{
                                                                                label: 'Precision',
                                                                                type: 'number',
                                                                                variant: 'outlined',
                                                                                size: 'small',
                                                                                valueAsNumber: true,
                                                                                placeholder: defaultDecimalPlaces !== null ? String(defaultDecimalPlaces) : 'None',
                                                                                error: !!decimalPlacesError,
                                                                                helperText: decimalPlacesError?.message,
                                                                                fullWidth: true,
                                                                                inputProps: { min: 0, max: 6, step: 1 },
                                                                                sx: { bgcolor: 'background.paper' },
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        )}
                                                        <Accordion disableGutters elevation={0} square sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                                            <AccordionSummary expandIcon={<ExpandMore fontSize="small" />} sx={{ minHeight: 0, p: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                <Typography variant="caption" fontWeight="medium">
                                                                    QA Settings ({qaEnabledCount}/3)
                                                                </Typography>
                                                            </AccordionSummary>
                                                            <AccordionDetails sx={{ p: 0 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                    <FormInput
                                                                        name={`${name}.${component.key}.max_retests`}
                                                                        props={{
                                                                            label: '# Retests',
                                                                            type: 'number',
                                                                            variant: 'outlined',
                                                                            size: 'small',
                                                                            valueAsNumber: true,
                                                                            placeholder: '0',
                                                                            fullWidth: true,
                                                                            disabled: !!field.value?.[component.key]?.unlimited_retests,
                                                                            inputProps: { min: 0, step: 1 },
                                                                            sx: { bgcolor: 'background.paper' },
                                                                        }}
                                                                    />
                                                                    <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                                                                        <FormControlLabel
                                                                            control={<Checkbox checked={!!field.value?.[component.key]?.unlimited_retests} onChange={(e) => setValue(component.key, 'unlimited_retests', e.target.checked)} size="small" />}
                                                                            label="Unlimited?"
                                                                            slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                            sx={{ whiteSpace: 'nowrap' }}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                                <Box>
                                                                    <FormControlLabel
                                                                        control={<Checkbox checked={!!field.value?.[component.key]?.fails_rapid_test} onChange={(e) => setValue(component.key, 'fails_rapid_test', e.target.checked)} size="small" />}
                                                                        label="Fails test?"
                                                                        slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                        sx={{ whiteSpace: 'nowrap' }}
                                                                    />
                                                                    <FormControlLabel
                                                                        control={<Checkbox checked={!!field.value?.[component.key]?.qa_overrideable} onChange={(e) => setValue(component.key, 'qa_overrideable', e.target.checked)} size="small" />}
                                                                        label="QA Overrideable?"
                                                                        slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                        sx={{ whiteSpace: 'nowrap' }}
                                                                    />
                                                                    <FormControlLabel
                                                                        control={<Checkbox checked={!!field.value?.[component.key]?.requires_two_qa_tests} onChange={(e) => setValue(component.key, 'requires_two_qa_tests', e.target.checked)} size="small" />}
                                                                        label="Requires 2 QA Tests?"
                                                                        slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                        sx={{ whiteSpace: 'nowrap' }}
                                                                    />
                                                                </Box>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                        {isUda && (
                                                            <Accordion disableGutters elevation={0} square sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                                                                <AccordionSummary expandIcon={<ExpandMore fontSize="small" />} sx={{ minHeight: 0, p: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="caption" fontWeight="medium">
                                                                        COA Settings ({coaEnabledCount}/{coaTotal})
                                                                    </Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 0 }}>
                                                                    <FormControlLabel
                                                                        control={<Checkbox checked={field.value?.[component.key]?.coa_settings?.show !== false} onChange={(e) => setCoaSetting(component.key, ['show'], e.target.checked)} size="small" />}
                                                                        label="Show on COA Report"
                                                                        slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                        sx={{ whiteSpace: 'nowrap' }}
                                                                    />
                                                                    {component.type === 'numeric' && (
                                                                        <>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <FormControlLabel
                                                                                    control={<Checkbox checked={coaFloorEnabled} onChange={(e) => setCoaSetting(component.key, ['floor', 'enable'], e.target.checked)} size="small" />}
                                                                                    label="Display '<' below"
                                                                                    slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                                                                                />
                                                                                {coaFloorEnabled && (
                                                                                    <TextField
                                                                                        type="number"
                                                                                        size="small"
                                                                                        label="Threshold"
                                                                                        value={componentStandard.coa_settings?.floor?.threshold ?? ''}
                                                                                        onChange={(e) => setCoaSetting(component.key, ['floor', 'threshold'], e.target.value === '' ? null : parseFloat(e.target.value))}
                                                                                        inputProps={{ step: 0.01 }}
                                                                                        sx={{ bgcolor: 'background.paper', maxWidth: 120 }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <FormControlLabel
                                                                                    control={<Checkbox checked={coaCeilingEnabled} onChange={(e) => setCoaSetting(component.key, ['ceiling', 'enable'], e.target.checked)} size="small" />}
                                                                                    label="Display '>' above"
                                                                                    slotProps={{ typography: { variant: 'caption', noWrap: true } }}
                                                                                    sx={{ whiteSpace: 'nowrap', mr: 0 }}
                                                                                />
                                                                                {coaCeilingEnabled && (
                                                                                    <TextField
                                                                                        type="number"
                                                                                        size="small"
                                                                                        label="Threshold"
                                                                                        value={componentStandard.coa_settings?.ceiling?.threshold ?? ''}
                                                                                        onChange={(e) => setCoaSetting(component.key, ['ceiling', 'threshold'], e.target.value === '' ? null : parseFloat(e.target.value))}
                                                                                        inputProps={{ step: 0.01 }}
                                                                                        sx={{ bgcolor: 'background.paper', maxWidth: 120 }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        </>
                                                                    )}
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        )}
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    );
                }}
            />
        </FormControl>
    );
};

export default RapidTestStandardsInput;
